// Type for the feature name components - do not hesitate to add more via union if needed
type MainFeatureName =
  | "feature_flag"
  | "ethermail"
  | "ton"
  | "oauth"
  | "api_keys"
  | "trade_reporting"
  | "improve_ux"
  | "table_display"
  | "campaign_rules"
  | "eligibility"
  | "stp"
  | "referral"
  | "telegram"
  | "trade_page"
  | "orderbook"
  | "campaign_partner"
  | "quests"
  | "socials"
  | "lottery"
  | "hte"
  | "hall_of_fame"
  | "hub_banner"
  | "spinning_wheel"
  | "affiliate"
  | "maintenance_mode"
  | "bot_detection"
  | "external_campaigns"
  | "claims"
  | "hyperliquid"
  | "campaign_search"
  | "ambassador"
  | "trade_to_earn"
  | "cede_flow_trade_and_earn"
  | "automatic_streak"
  | "performance"
  | "cex_perps"
  | "predictions"
  | "launch"
  | "exclusive_notifications"
  | "onchain_swap"
  | "parameter_store_cleanup"
  | "rate_limit"
  | "airdrop_farming"
  | "cryptocom_referee"
  | "kraken_page"
  | "coinbase_page"
  | "lbank_page"
  | "arbitrage"
  | "cross_redirect"
  | "leaderboard"
  | "perps"
  | "cede_arena"
  | "cede_arena_competition"
  | "cede_hub"
  | "experiment";

// Type for AppName to enforce consistent naming - do not hesitate to add more via union if needed
type AppName = "api" | "hub" | "flow" | "arena";

type FeatureDescription = string;

type FeatureFlagName = `${MainFeatureName}:${AppName}:${FeatureDescription}`;

export type FeatureFlagCatalogRequirement = {
  [featureFlag: FeatureFlagName]: string | boolean | number;
};

// Default values are used for inference.
//
// `satisfies` WITHOUT a type annotation, deliberately. Annotating this as
// `FeatureFlagCatalogRequirement` made `typeof featureFlagCatalog` collapse to that
// index signature, so `keyof FeatureFlagCatalog` became "any main:app:description
// string" and the `satisfies Record<string, keyof FeatureFlagCatalog>` on FEATURE_FLAGS
// below accepted a flag that was never added here — which then has no default to fall
// back on when the CMS read fails. `satisfies` alone keeps the literal keys and still
// checks the shape.
export const featureFlagCatalog = {
  "feature_flag:api:test": "",
  "ethermail:hub:enable": false,
  "ton:hub:enable": false,
  "oauth:hub:bitget": false,
  "oauth:hub:binance": false,
  "oauth:hub:bybit": false,
  "oauth:hub:mexc": false,
  "oauth:hub:gateio": false,
  "oauth:hub:kucoin": false,
  "oauth:hub:cryptocom": false,
  "oauth:hub:kraken": false,
  "trade_reporting:api:compute_volume_by_trades": false,
  // Minimum delay, in minutes, between two runs of the supervisor that
  // periodically re-runs external-order detection for all live PnL+perps
  // (campaign × epoch) tuples. The supervisor cron ticks every 5 min and
  // bails when the elapsed time since the last completed dispatch is below
  // this threshold. `0` disables throttling.
  "trade_reporting:api:external_order_detection_supervisor_min_interval_minutes": 30,
  "improve_ux:hub:new_onboarding": false,
  "improve_ux:hub:token_to_locked": false,
  "api_keys:hub:bitget": false,
  "api_keys:hub:binance": false,
  "api_keys:hub:bybit": false,
  "api_keys:hub:mexc": false,
  "api_keys:hub:gateio": false,
  "api_keys:hub:kucoin": false,
  "api_keys:hub:cryptocom": false,
  "api_keys:hub:kraken": false,
  "table_display:hub:start": false,
  "table_display:hub:current_traded_volume": true,
  "table_display:hub:daily_reward": true,
  "table_display:hub:epoch_ends": true,
  "table_display:hub:rule": true,
  "table_display:hub:participating": true,
  "campaign_rules:hub:show_max_total_volume_by_epoch": false,
  "eligibility:hub:profile_socials": false,
  "stp:api:binance": false,
  "stp:api:bybit": false,
  "stp:api:bitget": false,
  "stp:api:gateio": false,
  "stp:api:kucoin": false,
  "stp:api:cryptocom": false,
  "stp:api:mexc": false,
  "stp:api:add_user_in_stp_group_on_add_exchange_account": false,
  "stp:api:add_user_in_stp_group_on_trade_reporting": false,
  "stp:api:filter_out_events_with_no_stp_group_id": false,
  "stp:hub:auto_remove_exchange_account_for_security_reason": false,
  "trade_reporting:api:using_bullmq": false,
  // Gates emission of the `Trade Executed` Mixpanel event on hub/flow surfaces
  // (autopilot backend emitters + hub-v2/flow frontend trackEvent sites).
  // Off = no Trade Executed reporting. The backend listener + BullMQ worker
  // skip `safeTrack` when off; the frontends mirror the same gate. Arena has
  // its own dedicated gate below.
  "trade_reporting:api:trade_executed_event_enable": false,
  // Dedicated gate for the arena app's client-side Trade Executed reporting,
  // controlled independently from the :api: gate above. Frontend-only.
  "trade_reporting:arena:trade_executed_event_enable": false,
  "telegram:hub:trade_button": false,
  "referral:hub:enable": false,
  "referral:api:enable": false,
  "referral:api:code_generation_enable": false,
  "improve_ux:hub:claimable_data_in_profile": false,
  "trade_page:hub:enable": false,
  "predictions:hub:enable": false,
  "stp:api:keno_wash_trade_detection": false,
  "improve_ux:hub:max_trade_amount": false,
  "orderbook:hub:enable": false,
  "campaign_partner:hub:enable": false,
  "campaign_partner:hub:show_modal": false,
  "improve_ux:hub:hub_updates_modal": false,
  "quests:hub:enable": false,
  "quests:hub:daily_login": false,
  "socials:api:check_account_age": true,
  "socials:api:check_disconnection_cooldown": true,
  "lottery:hub:redeem_voucher": false,
  "lottery:hub:enable": false,
  "improve_ux:hub:cex_token_banners": false,
  "hub_banner:hub:enable": false,
  "hte:hub:enable": false,
  "hall_of_fame:hub:enable": true,
  "eligibility:api:cex_check_zero_fee_and_stablecoins": true,
  "eligibility:api:dex_check_stablecoins": true,
  "eligibility:api:disabled_checking_exchanges": "",
  "spinning_wheel:hub:enable": false,
  "spinning_wheel:api:enable": true,
  "affiliate:api:gateio": false,
  "affiliate:api:bitget": false,
  "affiliate:api:bybit": false,
  "affiliate:api:kucoin": false,
  "maintenance_mode:api:enable": false,
  "bot_detection:api:enable": false,
  "external_campaigns:hub:enable": false,
  "trade_page:hub:remove_hard_stop": true,
  "trade_page:hub:rewards_banner": false,
  "claims:hub:enable": false,
  "hyperliquid:hub:enable": false,
  "campaign_search:hub:enable": false,
  "campaign_search:api:enable": false,
  "ambassador:hub:highlight_addresses": false,
  "hall_of_fame:hub:countdown": false,
  "trade_to_earn:hub:restricted_campaign_access": false,
  "trade_to_earn:api:use_solo_cms": false,
  "automatic_streak:hub:enable": false,
  "automatic_streak:api:enable": false,
  "automatic_streak:api:captcha_enforce": false,
  "automatic_streak:api:order_splitting_enable": false,
  "automatic_streak:api:order_split_delay_min_ms": 10000,
  "automatic_streak:api:order_split_delay_max_ms": 45000,
  "automatic_streak:api:priority_tickets_enable": false,
  "hall_of_fame:hub:disabled": false,
  "performance:hub:disable_lottie_animations": false,
  "improve_ux:hub:realtime_stats": false,
  "cex_perps:hub:supported_exchanges": "",
  "launch:hub:self_register_enable": false,
  "exclusive_notifications:hub:enable": true,
  "spinning_wheel:hub:enable_recent_winners": false,
  "affiliate:api:mexc": false,
  "affiliate:api:lbank": false,
  "onchain_swap:hub:approvals_enable": false,
  // Defaults on: the panel reads the pool client-side, so this is a kill switch for
  // when the public Base RPCs misbehave, not a rollout gate.
  "onchain_swap:hub:swap_impact_enable": true,
  "parameter_store_cleanup:api:enable": false,
  "rate_limit:api:disable": false,
  "airdrop_farming:hub:enable": false,
  "airdrop_farming:hub:cex_spot_enable": false,
  "airdrop_farming:api:cex_spot_mexc_enable": false,
  "airdrop_farming:api:cex_spot_cryptocom_enable": false,
  "airdrop_farming:api:cex_spot_kucoin_enable": false,
  "airdrop_farming:api:cex_spot_bybit_enable": false,
  "airdrop_farming:api:strategy_grid_enable": true,
  "airdrop_farming:api:strategy_delta_neutral_enable": true,
  "airdrop_farming:api:exchange_hyperliquid_mm_enable": true,
  "airdrop_farming:api:exchange_hyperliquid_dn_enable": true,
  "airdrop_farming:api:exchange_paradex_mm_enable": true,
  "airdrop_farming:api:exchange_paradex_dn_enable": true,
  "airdrop_farming:api:exchange_grvt_mm_enable": true,
  "airdrop_farming:api:exchange_grvt_dn_enable": true,
  "airdrop_farming:api:exchange_zeroone_mm_enable": true,
  "airdrop_farming:api:exchange_zeroone_dn_enable": true,
  "airdrop_farming:api:exchange_extended_mm_enable": true,
  "airdrop_farming:api:exchange_extended_dn_enable": true,
  "arbitrage:hub:funding_rates": false,
  "airdrop_farming:api:exchange_hibachi_mm_enable": true,
  "airdrop_farming:api:exchange_hibachi_dn_enable": true,
  "airdrop_farming:api:exchange_hotstuff_mm_enable": false,
  "airdrop_farming:api:exchange_hotstuff_dn_enable": false,
  "airdrop_farming:api:exchange_bitget_mm_enable": true,
  "airdrop_farming:api:exchange_bitget_dn_enable": true,
  "airdrop_farming:api:exchange_decibel_mm_enable": true,
  "airdrop_farming:api:exchange_decibel_dn_enable": false,
  "airdrop_farming:api:exchange_rise_mm_enable": false,
  "airdrop_farming:api:exchange_rise_dn_enable": false,
  "airdrop_farming:flow:bot_creation_disable": false,
  "airdrop_farming:flow:exchange_hyperliquid_mm_maintenance": false,
  "airdrop_farming:flow:exchange_hyperliquid_dn_maintenance": false,
  "airdrop_farming:flow:exchange_paradex_mm_maintenance": false,
  "airdrop_farming:flow:exchange_paradex_dn_maintenance": false,
  "airdrop_farming:flow:exchange_grvt_mm_maintenance": false,
  "airdrop_farming:flow:exchange_grvt_dn_maintenance": false,
  "airdrop_farming:flow:exchange_zeroone_mm_maintenance": false,
  "airdrop_farming:flow:exchange_zeroone_dn_maintenance": false,
  "airdrop_farming:flow:exchange_extended_mm_maintenance": false,
  "airdrop_farming:flow:exchange_extended_dn_maintenance": false,
  "airdrop_farming:flow:exchange_hibachi_mm_maintenance": false,
  "airdrop_farming:flow:exchange_hibachi_dn_maintenance": false,
  "airdrop_farming:flow:exchange_hotstuff_mm_maintenance": false,
  "airdrop_farming:flow:exchange_hotstuff_dn_maintenance": false,
  "airdrop_farming:flow:exchange_bitget_mm_maintenance": false,
  "airdrop_farming:flow:exchange_bitget_dn_maintenance": false,
  "airdrop_farming:flow:hibachi_promo_banner": false,
  "airdrop_farming:flow:exchange_decibel_mm_maintenance": false,
  "airdrop_farming:flow:exchange_decibel_dn_maintenance": false,
  "airdrop_farming:flow:exchange_rise_mm_maintenance": false,
  "airdrop_farming:flow:exchange_rise_dn_maintenance": false,
  "cryptocom_referee:hub:enable": false,
  "cryptocom_referee:hub:reward_amount": "50",
  "kraken_page:hub:enable": false,
  "coinbase_page:hub:enable": false,
  "lbank_page:hub:enable": false,
  "cross_redirect:hub:flow_redirect_enable": false,
  "referral:flow:enable": false,
  "leaderboard:flow:enable": false,
  "cede_flow_trade_and_earn:flow:enable": false,
  "leaderboard:flow:exchange_filter_enable": false,
  "airdrop_farming:flow:backtest_results_enable": false,
  "airdrop_farming:flow:force_bot_rating": false,
  "airdrop_farming:flow:hotstuff_promo_banner": false,
  "airdrop_farming:flow:promo_banner_carousel_enable": false,
  // Cede Arena cross-promo slide in Flow's promo carousel. The carousel hosts it,
  // so `airdrop_farming:flow:promo_banner_carousel_enable` must be on as well.
  "cede_arena:flow:promo_banner": false,
  "leaderboard:api:price_recompute_enabled": false,
  "perps:hub:disable_tp_sl": false,
  "perps:hub:disable_limit_orders": false,
  "hyperliquid:hub:margin_mode_enable": false,
  // Airdrop-farming persisted-leaderboard (order-fill based). Global boolean
  // gate + per-exchange reporting window. Window value format: ISO|ISO
  // (pipe-separated UTC timestamps), e.g. "2026-04-01T00:00:00Z|2026-05-01T00:00:00Z".
  // Empty string = reporting disabled for that exchange.
  "airdrop_farming:api:leaderboard_enable": false,
  "airdrop_farming:api:leaderboard_hyperliquid_window": "",
  "airdrop_farming:api:leaderboard_paradex_window": "",
  "airdrop_farming:api:leaderboard_grvt_window": "",
  "airdrop_farming:api:leaderboard_zeroone_window": "",
  "airdrop_farming:api:leaderboard_extended_window": "",
  "airdrop_farming:api:leaderboard_hibachi_window": "",
  "airdrop_farming:api:leaderboard_decibel_window": "",
  "airdrop_farming:api:leaderboard_bitget_window": "",
  "airdrop_farming:api:leaderboard_mexc_window": "",
  "airdrop_farming:api:leaderboard_kucoin_window": "",
  "airdrop_farming:api:leaderboard_cryptocom_window": "",
  "airdrop_farming:api:leaderboard_bybit_window": "",
  "airdrop_farming:api:leaderboard_hotstuff_window": "",
  "socials:flow:discord_link_enable": false,
  "socials:flow:x_link_enable": false,
  "socials:flow:telegram_link_enable": false,
  // Arena's connect buttons (profile, survey contact ask). /100k is not gated by these.
  "socials:arena:x_link_enable": false,
  "socials:arena:telegram_link_enable": false,
  "cede_arena:api:enable": false,
  "cede_arena:api:private_competition_enable": false,
  "cede_arena:api:rising_drawdown_enable": false,
  "cede_arena:api:canberrai_auto_match_enable": false,
  "cede_arena:api:rematch_enable": false,
  "cede_arena:api:forfeit_enable": false,
  "cede_arena:api:spin_and_go_enable": false,
  "cede_arena:api:auto_accept_match_enable": false,
  "cede_arena:api:canberrai_bot_competitions_enable": false,
  // Gates the lifetime bot-duel cap; off ⇒ the bot wait ladder alone holds farmers back.
  "cede_arena:api:canberrai_bot_duel_cap_enable": true,
  // Hover/tap player profile card shown wherever a username is rendered in Arena.
  // The api owns the gate because the profile route 404s when off; the arena FE
  // mirrors it to skip mounting the trigger.
  "cede_arena:api:player_profile_popup_enable": false,
  "cede_arena:hub:promo_banner": false,
  "cede_arena:arena:leaderboard_enable": false,
  "cede_arena:arena:payouts_feed_enable": false,
  "cede_arena:arena:weekly_leaderboard_enable": false,
  "cede_arena:arena:quests_enable": false,
  // Arena-only UI: the one-shot "were you invited?" ask shown to a new player who
  // arrived without an invite link, plus the confirmation a referred player sees. Pure
  // presentation, so the owner is arena: the api gate is cede_arena:api:referrals_enable
  // and it stays the authority on whether a code can be applied at all.
  // Show the email option in the Cede Arena Privy connect modal. UI-only
  // (Privy `loginMethods`) — the backend still accepts email-OTP sign-in
  // regardless, so this is an arena frontend flag.
  "cede_arena:arena:email_login_enable": false,
  // Serve Arena's own sign-in UI (headless Privy) instead of Privy's stock
  // modal. Off ⇒ `login()` opens Privy exactly as today, so this is the kill
  // switch for the whole custom surface. FE-only: the backend sign-in contract
  // is identical either way.
  "cede_arena:arena:custom_sign_in_enable": false,
  // Social sign-in methods, one switch each. Each needs the provider enabled on the
  // Privy app for that environment (Telegram: its own OIDC client from BotFather).
  // FE-only: the backend resolves any Privy login by its user id.
  "cede_arena:arena:google_login_enable": false,
  "cede_arena:arena:x_login_enable": false,
  "cede_arena:arena:telegram_login_enable": false,
  "cede_arena_competition:flow:enable": false,
  // Unifold availability. Serves the deposit modal's crypto method, and its card
  // method in the countries listed below; off ⇒ crypto is the on-chain USDT transfer.
  "cede_arena:arena:deposit_via_unifold_enable": false,
  // Crossmint card / Apple Pay onramp into the user's Privy embedded wallet. The API
  // refuses Crossmint setup and orders while off; with Unifold also off, the deposit
  // modal offers crypto alone.
  "cede_arena:api:deposit_via_crossmint_enable": false,
  // Comma-separated ISO country codes whose CARD deposits go to Unifold even while
  // Crossmint is on. Empty = Crossmint everywhere. Edited from the Solo CMS Arena
  // Deposits page.
  "cede_arena:api:deposit_unifold_countries": "",
  // The legacy on-chain USDT transfer, which backs the crypto method while Unifold is off. The
  // row is seeded on by migration, because an absent row reads as off and would retire a live rail.
  "cede_arena:api:deposit_via_onchain_enable": true,
  // Newline-separated trading tips rotated in the Arena matchmaking waiting
  // room and get-ready overlay. Empty = Arena's built-in curated list.
  "cede_arena:arena:matchmaking_trading_tips": "",
  // Gates the free (Casual) matchmaking tier in the Arena stake picker. When
  // off, the Casual/Free tile is locked. FE-only switch.
  "cede_arena:arena:free_matchmaking_enable": false,
  // Gates the promotional announcement modal + "Exclusive battles" entry button
  // on the Arena My battles header. When off, neither is shown. FE-only switch.
  "cede_arena:arena:announcement_modal_enable": false,
  // Gates the in-competition chat. Backend refuses sends + 404s the chat
  // routes when off; the arena FE mirrors the gate to hide the chat UI.
  "cede_arena:api:chat_enable": false,
  // Chat AI moderation tier: on = paid LLM policy check (gpt-4o-mini),
  // off = free OpenAI Moderation API. The profanity word list always runs first.
  "cede_arena:api:chat_llm_moderation_enable": false,
  // Backend gate for AI bot chat participants: when off, the chat workers skip
  // all bot reply / opener work. Enforced by the api.
  "cede_arena:api:chat_bot_participants_enable": false,
  "cede_arena:api:chat_bot_opt_in_competitions_enable": false,
  "cede_arena:api:require_nickname_enable": false,
  // Withholds the opponent's identity from every payload of a live proposal
  // until both sides confirm, so a user can't decline to dodge a known rival.
  "cede_arena:api:hide_opponent_identity_enable": false,
  // Gates the guided onboarding flow (welcome modal + tour) in Arena. When off,
  // the flow never auto-opens. FE-only switch.
  "cede_arena:arena:onboarding_flow_enable": false,
  "cede_arena:arena:multi_duel_enable": false,
  "cede_arena:arena:post_competition_survey_enable": false,
  // Persona qualification form (DEV-703): the post-sign-in questionnaire. When
  // off the FE never mounts it and the endpoints are unreachable behind the
  // arena kill switch, so the module is inert.
  "cede_arena:arena:qualification_form_enable": false,
  // Arena entry tickets (DEV-651): master kill switch for minting AND spending
  // across starter packs, partner links and the quest — when off, all of it
  // refuses work. Enforced by the api; Arena mirrors it.
  "cede_arena:api:entry_tickets_enable": false,
  "cede_arena:api:ticket_links_enable": false,
  // DEV-734: a ticket-funded winner is paid its face value, never the nominal
  // pot. Off ⇒ the pre-cap payout, where the winner takes 2x a stake only one
  // side funded.
  "cede_arena:api:ticket_payout_cap_enable": false,
  "cede_arena:api:referrals_enable": false,
  // KOL managers (DEV-728): the API refuses every manager and invite route when off and
  // Arena hides the dashboard. Links already made keep accruing their split; Solo's unlink
  // and revoke stay available as the money kill path. Needs referrals_enable on too.
  "cede_arena:api:kol_managers_enable": false,
  // Cede Arena clans: the API refuses joins, leaves and tournament settlement when off,
  // and Arena hides the nav entry, the /clans route and the clan tags.
  "cede_arena:api:clans_enable": false,
  // Cede Arena instant withdrawals: small withdrawals are paid out in one transaction
  // instead of epoch + cooldown + claim. Needs the withdraw vault on a V3 rewards module.
  "cede_arena:api:instant_withdraw_enable": false,
  "cede_arena:api:onboarding_quest_enable": false,
  // Public, fully-anonymous "someone joined the queue" post to the community
  // Discord on every real paid queue-join. Backend gate: when off, the API skips
  // the post. Kill switch for a noisy public feed — safe default off.
  "cede_arena:api:public_queue_join_notification_enable": false,
  // Gates the Arena support chat end-to-end: when off the API 404s every
  // support route and the arena widget never mounts.
  "cede_arena:api:support_chat_enable": false,
  // Cede Support AI agent kill switch: when off, no agent run is enqueued and
  // ops answer every conversation. Backend gate.
  "cede_arena:api:support_agent_enable": false,
  // Shadow mode: the agent runs the full pipeline and stores its draft on the
  // run for ops to compare, but never posts to the user. Backend gate.
  "cede_arena:api:support_agent_shadow": false,
  // Arena friend graph (DEV-708): master gate for the friends list, requests,
  // nickname lookup and invite links. Backend-enforced — every friends route
  // 404s when off; the arena FE mirrors it to hide the header button.
  "cede_arena:api:friends_enable": false,
  // Same anonymous "someone joined the queue" feed as the flag above, posted to
  // the SEPARATE Cede Hub community Discord. Independent kill switch so the two
  // servers can be toggled apart. Backend gate: when off, the API skips the Hub
  // post. Safe default off.
  "cede_hub:api:cede_arena_public_queue_join_notification_enable": false,
  // Who a finalized paid Arena competition's autopilot priority tickets go to:
  // on ⇒ every non-bot entrant that played a positive fee; off ⇒ only the
  // rewarded ranks. Off = shipped behaviour.
  "cede_arena:api:priority_tickets_all_participants_enable": false,
  // Big Arena Competition whitelist registrations (`/100k`): off means the
  // submit endpoint refuses and the page shows "registrations closed". Ship
  // dark, flip on at announcement, off when the list is final.
  "cede_arena:api:big_competition_registration_enable": false,
  // The `/100k` promo banner in the duels lobby. Independent of the
  // registration flag: flip it off when the banner should stop promoting.
  "cede_arena:arena:big_competition_promo_banner_enable": false,
  // Multi-round tournaments: off refuses joins and CMS actions and stops the
  // scoring, round-close and payout jobs. Kill switch for the whole engine.
  "cede_arena:api:tournament_enable": false,
  // The `/tournament/[slug]` page and the lobby banner's switch to "Join the tournament".
  "cede_arena:arena:tournament_page_enable": false,
  // Gold highlight + hot-first sort in the duel market selector, ranked by paper-trading pair volatility. FE-only.
  "cede_arena:arena:hot_pairs_enable": false,
  // `/5k` sign-up registrations: off means the submit endpoint refuses and the
  // page shows "registrations closed". Independent of the `/100k` whitelist flag.
  "cede_arena:api:tournament_5k_registration_enable": false,
  // The `/5k` prize pool in USDT, as printed on the page, its OG card and SEO.
  // 0 means unset: `/5k` answers 404 until ops sets it.
  "cede_arena:arena:tournament_5k_prize_pool_usdt": 0,
  // Favorites filter + star buttons in the duel market selector. FE-only.
  "cede_arena:arena:market_favorites_enable": false,
  // Arena lobby Feed: gates GET /v1/cede-arena/feed, the arena SSE leg and the UI; writers always record.
  "cede_arena:api:global_feed_enable": false,
  // Arena GLOBAL chat room: off ⇒ GLOBAL sends 404, the feed omits CHAT and the SSE chat leg is not merged.
  "cede_arena:api:global_chat_enable": false,
  // Spectator chat: off ⇒ only a duel's roster may post in its chat.
  "cede_arena:api:spectator_chat_enable": false,
  // Pre-start chat: on ⇒ a competition's chat opens 30 min before its start; joined players post, others per the spectator flag.
  "cede_arena:api:pre_start_chat_enable": false,
  // Waiting room roster: on ⇒ the pre-start stage shows who joined and the centred clock, plus Room chat with pre-start chat on.
  "cede_arena:arena:pre_start_roster_enable": false,
  // Arena mobile shell (bottom bar, place chooser, Feed/Menu tabs). FE-only rollback switch.
  "cede_arena:arena:mobile_nav_v2_enable": false,
  // Battle Royale: gates creation only; existing BR blueprints keep generating (disable the blueprint to stop them).
  "cede_arena:api:battle_royale_enable": false,
  // Arena simple (Up / Down) trade mode beside the advanced ticket. FE-only.
  "cede_arena:arena:simple_trade_mode_enable": false,
  // Classic PAID 1v1 duel; off hides the PAID Standard entry and the API refuses human PAID opt-ins and rematches.
  "cede_arena:api:classic_paid_duel_enable": true,
  // Battle Royale-led home at /play; off is the mode-card lobby. FE-only, the API serves every mode either way.
  // Flip with classic_paid_duel_enable OFF: this home offers no classic PAID entry, nothing ties the two.
  "cede_arena:arena:br_home_enable": false,
  // Opponent activity + live Arena redaction (positions withheld, LIVE_PNL frames, no price ticks); off ⇒ none of it.
  "cede_arena:api:opponent_activity_enable": false,
  // Live board rows (heat ring, leverage badge, holding clock) and the row card. FE-only.
  "cede_arena:arena:opponent_live_board_enable": false,
  // Stored post-battle record (trades, PnL series, cuts) built at finalization and served after the bell; off => none.
  "cede_arena:api:battle_record_enable": false,
  // Post-battle reveal for 3+ player battles: best moments, podium standings, full breakdown. FE-only.
  "cede_arena:arena:post_battle_reveal_enable": false,
  // Waiting room: the pre-start stage on every competition page, matches included; off is the old overlays. FE-only.
  "cede_arena:arena:waiting_room_enable": false,
  // Prepare: the prep desk from the waiting room (scheduled, Battle Royale, private rooms). Needs the waiting room. FE-only.
  "cede_arena:arena:pre_start_desk_enable": false,
  // Armed opening move, placed at the bell; off ⇒ the API refuses arming and the arena hides "Create opening move".
  "cede_arena:api:armed_opening_move_enable": false,
  // Share cards v1: the server-rendered card, its editor and the result-aware battle link preview. FE-only.
  "cede_arena:arena:share_cards_enable": false,
  "experiment:api:qa_variant_override_enable": false,
} satisfies FeatureFlagCatalogRequirement;

export type FeatureFlagCatalog = typeof featureFlagCatalog;

export const FEATURE_FLAGS = {
  TEST: "feature_flag:api:test",
  ETHERMAIL_HUB: "ethermail:hub:enable",
  TON_CONNECT_HUB: "ton:hub:enable",
  OAUTH_HUB_BITGET: "oauth:hub:bitget",
  OAUTH_HUB_BINANCE: "oauth:hub:binance",
  OAUTH_HUB_BYBIT: "oauth:hub:bybit",
  OAUTH_HUB_MEXC: "oauth:hub:mexc",
  OAUTH_HUB_GATEIO: "oauth:hub:gateio",
  OAUTH_HUB_KUCOIN: "oauth:hub:kucoin",
  OAUTH_HUB_CRYPTOCOM: "oauth:hub:cryptocom",
  OAUTH_HUB_KRAKEN: "oauth:hub:kraken",
  TRADE_REPORTING_API_COMPUTE_VOLUME_BY_TRADES: "trade_reporting:api:compute_volume_by_trades",
  TRADE_REPORTING_API_EXTERNAL_ORDER_DETECTION_SUPERVISOR_MIN_INTERVAL_MINUTES:
    "trade_reporting:api:external_order_detection_supervisor_min_interval_minutes",
  IMPROVE_UX_HUB_TOKEN_TO_LOCKED: "improve_ux:hub:token_to_locked",
  API_KEYS_HUB_BITGET: "api_keys:hub:bitget",
  API_KEYS_HUB_BINANCE: "api_keys:hub:binance",
  API_KEYS_HUB_BYBIT: "api_keys:hub:bybit",
  API_KEYS_HUB_MEXC: "api_keys:hub:mexc",
  API_KEYS_HUB_GATEIO: "api_keys:hub:gateio",
  API_KEYS_HUB_KUCOIN: "api_keys:hub:kucoin",
  API_KEYS_HUB_CRYPTOCOM: "api_keys:hub:cryptocom",
  API_KEYS_HUB_KRAKEN: "api_keys:hub:kraken",
  TABLE_DISPLAY_HUB_START: "table_display:hub:start",
  TABLE_DISPLAY_HUB_CURRENT_TRADED_VOLUME: "table_display:hub:current_traded_volume",
  TABLE_DISPLAY_HUB_DAILY_REWARD: "table_display:hub:daily_reward",
  TABLE_DISPLAY_HUB_EPOCH_ENDS: "table_display:hub:epoch_ends",
  TABLE_DISPLAY_HUB_RULE: "table_display:hub:rule",
  TABLE_DISPLAY_HUB_PARTICIPATING: "table_display:hub:participating",
  CAMPAIGN_RULES_HUB_SHOW_MAX_TOTAL_VOLUME_BY_EPOCH: "campaign_rules:hub:show_max_total_volume_by_epoch",
  ELIGIBILITY_HUB_PROFILE_SOCIALS: "eligibility:hub:profile_socials",
  STP_API_BINANCE: "stp:api:binance",
  STP_API_BYBIT: "stp:api:bybit",
  STP_API_BITGET: "stp:api:bitget",
  STP_API_GATEIO: "stp:api:gateio",
  STP_API_KUCOIN: "stp:api:kucoin",
  STP_API_CRYPTOCOM: "stp:api:cryptocom",
  STP_API_MEXC: "stp:api:mexc",
  STP_API_ADD_USER_IN_STP_GROUP_ON_ADD_EXCHANGE_ACCOUNT: "stp:api:add_user_in_stp_group_on_add_exchange_account",
  STP_API_ADD_USER_IN_STP_GROUP_ON_TRADE_REPORTING: "stp:api:add_user_in_stp_group_on_trade_reporting",
  FILTER_OUT_EVENTS_WITH_NO_STP_GROUP_ID: "stp:api:filter_out_events_with_no_stp_group_id",
  AUTO_REMOVE_EXCHANGE_ACCOUNT_FOR_SECURITY_REASON: "stp:hub:auto_remove_exchange_account_for_security_reason",
  TRADE_REPORTING_USING_BULLMQ: "trade_reporting:api:using_bullmq",
  TRADE_REPORTING_API_TRADE_EXECUTED_EVENT_ENABLE: "trade_reporting:api:trade_executed_event_enable",
  TRADE_REPORTING_ARENA_TRADE_EXECUTED_EVENT_ENABLE: "trade_reporting:arena:trade_executed_event_enable",
  TELEGRAM_HUB_TRADE_BUTTON: "telegram:hub:trade_button",
  REFERRAL_HUB_ENABLE: "referral:hub:enable",
  REFERRAL_API_ENABLE: "referral:api:enable",
  REFERRAL_CODE_GENERATION_ENABLE: "referral:api:code_generation_enable",
  IMPROVE_UX_HUB_CLAIMABLE_DATA_IN_PROFILE: "improve_ux:hub:claimable_data_in_profile",
  TRADE_PAGE_HUB_ENABLE: "trade_page:hub:enable",
  PREDICTIONS_HUB_ENABLE: "predictions:hub:enable",
  STP_API_KENOBI_ENABLE: "stp:api:keno_wash_trade_detection",
  IMPROVE_UX_HUB_MAX_TRADE_AMOUNT: "improve_ux:hub:max_trade_amount",
  ORDERBOOK_HUB_ENABLE: "orderbook:hub:enable",
  CAMPAIGN_PARTNER_HUB_ENABLE: "campaign_partner:hub:enable",
  CAMPAIGN_PARTNER_HUB_SHOW_MODAL: "campaign_partner:hub:show_modal",
  IMPROVE_UX_HUB_HUB_UPDATES_MODAL: "improve_ux:hub:hub_updates_modal",
  QUESTS_HUB_ENABLE: "quests:hub:enable",
  QUESTS_HUB_DAILY_LOGIN: "quests:hub:daily_login",
  LOTTERY_HUB_REDEEM_VOUCHER: "lottery:hub:redeem_voucher",
  LOTTERY_HUB_ENABLE: "lottery:hub:enable",
  SOCIALS_API_CHECK_ACCOUNT_AGE: "socials:api:check_account_age",
  SOCIALS_API_CHECK_DISCONNECTION_COOLDOWN: "socials:api:check_disconnection_cooldown",
  IMPROVE_UX_HUB_CEX_TOKEN_BANNERS: "improve_ux:hub:cex_token_banners",
  HUB_BANNER_HUB_ENABLE: "hub_banner:hub:enable",
  HTE_HUB_ENABLE: "hte:hub:enable",
  HALL_OF_FAME_HUB_ENABLE: "hall_of_fame:hub:enable",
  ELIGIBILITY_API_CEX_CHECK_ZERO_FEE_AND_STABLECOINS: "eligibility:api:cex_check_zero_fee_and_stablecoins",
  ELIGIBILITY_API_DEX_CHECK_STABLECOINS: "eligibility:api:dex_check_stablecoins",
  ELIGIBILITY_API_DISABLED_CHECKING_EXCHANGES: "eligibility:api:disabled_checking_exchanges",
  SPINNING_WHEEL_HUB_ENABLE: "spinning_wheel:hub:enable",
  SPINNING_WHEEL_API_ENABLE: "spinning_wheel:api:enable",
  SPINNING_WHEEL_HUB_RECENT_WINNERS_ENABLE: "spinning_wheel:hub:enable_recent_winners",
  AFFILIATE_API_GATEIO: "affiliate:api:gateio",
  AFFILIATE_API_BITGET: "affiliate:api:bitget",
  AFFILIATE_API_BYBIT: "affiliate:api:bybit",
  AFFILIATE_API_KUCOIN: "affiliate:api:kucoin",
  AFFILIATE_API_MEXC: "affiliate:api:mexc",
  AFFILIATE_API_LBANK: "affiliate:api:lbank",
  IMPROVE_UX_HUB_NEW_ONBOARDING: "improve_ux:hub:new_onboarding",
  MAINTENANCE_MODE_API_ENABLE: "maintenance_mode:api:enable",
  BOT_DETECTION_ENABLE: "bot_detection:api:enable",
  TRADE_PAGE_HUB_REMOVE_HARD_STOP: "trade_page:hub:remove_hard_stop",
  TRADE_PAGE_HUB_REWARDS_BANNER: "trade_page:hub:rewards_banner",
  CLAIMS_HUB_ENABLE: "claims:hub:enable",
  HYPERLIQUID_HUB_ENABLE: "hyperliquid:hub:enable",
  HYPERLIQUID_HUB_MARGIN_MODE_ENABLE: "hyperliquid:hub:margin_mode_enable",
  CAMPAIGN_SEARCH_HUB_ENABLE: "campaign_search:hub:enable",
  CAMPAIGN_SEARCH_API_ENABLE: "campaign_search:api:enable",
  AMBASSADOR_HUB_HIGHLIGHT: "ambassador:hub:highlight_addresses",
  HALL_OF_FAME_HUB_COUNTDOWN: "hall_of_fame:hub:countdown",
  HALL_OF_FAME_HUB_DISABLED: "hall_of_fame:hub:disabled",
  RESTRICTED_CAMPAIGN_ACCESS: "trade_to_earn:hub:restricted_campaign_access",
  TTE_API_USE_SOLO_CMS: "trade_to_earn:api:use_solo_cms",
  AUTOMATIC_STREAK_HUB_ENABLE: "automatic_streak:hub:enable",
  AUTOMATIC_STREAK_API_ENABLE: "automatic_streak:api:enable",
  AUTOMATIC_STREAK_API_CAPTCHA_ENFORCE: "automatic_streak:api:captcha_enforce",
  AUTOMATIC_STREAK_API_ORDER_SPLITTING_ENABLE: "automatic_streak:api:order_splitting_enable",
  AUTOMATIC_STREAK_API_ORDER_SPLIT_DELAY_MIN_MS: "automatic_streak:api:order_split_delay_min_ms",
  AUTOMATIC_STREAK_API_ORDER_SPLIT_DELAY_MAX_MS: "automatic_streak:api:order_split_delay_max_ms",
  /**
   * Master gate for autopilot priority tickets. When off: awards from paid Arena
   * competitions are skipped and ticket spends (join boost / top-up) are refused;
   * already-credited queue priority counters keep working (picks, consumption,
   * refunds). Frontends read this same flag to hide the ticket UI.
   */
  AUTOMATIC_STREAK_API_PRIORITY_TICKETS_ENABLE: "automatic_streak:api:priority_tickets_enable",
  PERFORMANCE_HUB_DISABLE_LOTTIE: "performance:hub:disable_lottie_animations",
  IMPROVE_UX_HUB_REALTIME_STATS: "improve_ux:hub:realtime_stats",
  ALL_CEX_PERPS_EXCHANGES: "cex_perps:hub:supported_exchanges",
  LAUNCH_HUB_SELF_REGISTER_ENABLE: "launch:hub:self_register_enable",
  EXCLUSIVE_NOTIFICATIONS_HUB_ENABLE: "exclusive_notifications:hub:enable",
  ONCHAIN_SWAP_HUB_APPROVALS_ENABLE: "onchain_swap:hub:approvals_enable",
  ONCHAIN_SWAP_HUB_SWAP_IMPACT_ENABLE: "onchain_swap:hub:swap_impact_enable",
  PARAMETER_STORE_CLEANUP_API_ENABLE: "parameter_store_cleanup:api:enable",
  RATE_LIMIT_API_DISABLE: "rate_limit:api:disable",
  AIRDROP_FARMING_HUB_ENABLE: "airdrop_farming:hub:enable",
  AIRDROP_FARMING_HUB_CEX_SPOT_ENABLE: "airdrop_farming:hub:cex_spot_enable",
  AIRDROP_FARMING_API_CEX_SPOT_MEXC_ENABLE: "airdrop_farming:api:cex_spot_mexc_enable",
  AIRDROP_FARMING_API_CEX_SPOT_KUCOIN_ENABLE: "airdrop_farming:api:cex_spot_kucoin_enable",
  AIRDROP_FARMING_API_CEX_SPOT_CRYPTOCOM_ENABLE: "airdrop_farming:api:cex_spot_cryptocom_enable",
  AIRDROP_FARMING_API_CEX_SPOT_BYBIT_ENABLE: "airdrop_farming:api:cex_spot_bybit_enable",
  AIRDROP_FARMING_API_STRATEGY_GRID_ENABLE: "airdrop_farming:api:strategy_grid_enable",
  AIRDROP_FARMING_API_STRATEGY_DELTA_NEUTRAL_ENABLE: "airdrop_farming:api:strategy_delta_neutral_enable",
  AIRDROP_FARMING_API_EXCHANGE_HYPERLIQUID_MM_ENABLE: "airdrop_farming:api:exchange_hyperliquid_mm_enable",
  AIRDROP_FARMING_API_EXCHANGE_HYPERLIQUID_DN_ENABLE: "airdrop_farming:api:exchange_hyperliquid_dn_enable",
  AIRDROP_FARMING_API_EXCHANGE_PARADEX_MM_ENABLE: "airdrop_farming:api:exchange_paradex_mm_enable",
  AIRDROP_FARMING_API_EXCHANGE_PARADEX_DN_ENABLE: "airdrop_farming:api:exchange_paradex_dn_enable",
  AIRDROP_FARMING_API_EXCHANGE_GRVT_MM_ENABLE: "airdrop_farming:api:exchange_grvt_mm_enable",
  AIRDROP_FARMING_API_EXCHANGE_GRVT_DN_ENABLE: "airdrop_farming:api:exchange_grvt_dn_enable",
  AIRDROP_FARMING_API_EXCHANGE_ZEROONE_MM_ENABLE: "airdrop_farming:api:exchange_zeroone_mm_enable",
  AIRDROP_FARMING_API_EXCHANGE_ZEROONE_DN_ENABLE: "airdrop_farming:api:exchange_zeroone_dn_enable",
  AIRDROP_FARMING_API_EXCHANGE_EXTENDED_MM_ENABLE: "airdrop_farming:api:exchange_extended_mm_enable",
  AIRDROP_FARMING_API_EXCHANGE_EXTENDED_DN_ENABLE: "airdrop_farming:api:exchange_extended_dn_enable",
  ARBITRAGE_HUB_FUNDING_RATES: "arbitrage:hub:funding_rates",
  AIRDROP_FARMING_API_EXCHANGE_HIBACHI_MM_ENABLE: "airdrop_farming:api:exchange_hibachi_mm_enable",
  AIRDROP_FARMING_API_EXCHANGE_HIBACHI_DN_ENABLE: "airdrop_farming:api:exchange_hibachi_dn_enable",
  AIRDROP_FARMING_API_EXCHANGE_HOTSTUFF_MM_ENABLE: "airdrop_farming:api:exchange_hotstuff_mm_enable",
  AIRDROP_FARMING_API_EXCHANGE_HOTSTUFF_DN_ENABLE: "airdrop_farming:api:exchange_hotstuff_dn_enable",
  AIRDROP_FARMING_API_EXCHANGE_DECIBEL_MM_ENABLE: "airdrop_farming:api:exchange_decibel_mm_enable",
  AIRDROP_FARMING_API_EXCHANGE_DECIBEL_DN_ENABLE: "airdrop_farming:api:exchange_decibel_dn_enable",
  AIRDROP_FARMING_API_EXCHANGE_RISE_MM_ENABLE: "airdrop_farming:api:exchange_rise_mm_enable",
  AIRDROP_FARMING_API_EXCHANGE_RISE_DN_ENABLE: "airdrop_farming:api:exchange_rise_dn_enable",
  AIRDROP_FARMING_FLOW_BOT_CREATION_DISABLE: "airdrop_farming:flow:bot_creation_disable",
  AIRDROP_FARMING_FLOW_EXCHANGE_HYPERLIQUID_MM_MAINTENANCE: "airdrop_farming:flow:exchange_hyperliquid_mm_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_HYPERLIQUID_DN_MAINTENANCE: "airdrop_farming:flow:exchange_hyperliquid_dn_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_PARADEX_MM_MAINTENANCE: "airdrop_farming:flow:exchange_paradex_mm_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_PARADEX_DN_MAINTENANCE: "airdrop_farming:flow:exchange_paradex_dn_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_GRVT_MM_MAINTENANCE: "airdrop_farming:flow:exchange_grvt_mm_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_GRVT_DN_MAINTENANCE: "airdrop_farming:flow:exchange_grvt_dn_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_ZEROONE_MM_MAINTENANCE: "airdrop_farming:flow:exchange_zeroone_mm_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_ZEROONE_DN_MAINTENANCE: "airdrop_farming:flow:exchange_zeroone_dn_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_EXTENDED_MM_MAINTENANCE: "airdrop_farming:flow:exchange_extended_mm_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_EXTENDED_DN_MAINTENANCE: "airdrop_farming:flow:exchange_extended_dn_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_HIBACHI_MM_MAINTENANCE: "airdrop_farming:flow:exchange_hibachi_mm_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_HIBACHI_DN_MAINTENANCE: "airdrop_farming:flow:exchange_hibachi_dn_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_HOTSTUFF_MM_MAINTENANCE: "airdrop_farming:flow:exchange_hotstuff_mm_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_HOTSTUFF_DN_MAINTENANCE: "airdrop_farming:flow:exchange_hotstuff_dn_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_BITGET_MM_MAINTENANCE: "airdrop_farming:flow:exchange_bitget_mm_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_BITGET_DN_MAINTENANCE: "airdrop_farming:flow:exchange_bitget_dn_maintenance",
  AIRDROP_FARMING_FLOW_HIBACHI_PROMO_BANNER: "airdrop_farming:flow:hibachi_promo_banner",
  AIRDROP_FARMING_FLOW_HOTSTUFF_PROMO_BANNER: "airdrop_farming:flow:hotstuff_promo_banner",
  AIRDROP_FARMING_FLOW_PROMO_BANNER_CAROUSEL: "airdrop_farming:flow:promo_banner_carousel_enable",
  /** Cede Arena cross-promo slide in Flow's promo carousel. Requires the carousel flag too. */
  CEDE_ARENA_FLOW_PROMO_BANNER: "cede_arena:flow:promo_banner",
  AIRDROP_FARMING_API_EXCHANGE_BITGET_MM_ENABLE: "airdrop_farming:api:exchange_bitget_mm_enable",
  AIRDROP_FARMING_API_EXCHANGE_BITGET_DN_ENABLE: "airdrop_farming:api:exchange_bitget_dn_enable",
  AIRDROP_FARMING_FLOW_EXCHANGE_DECIBEL_MM_MAINTENANCE: "airdrop_farming:flow:exchange_decibel_mm_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_DECIBEL_DN_MAINTENANCE: "airdrop_farming:flow:exchange_decibel_dn_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_RISE_MM_MAINTENANCE: "airdrop_farming:flow:exchange_rise_mm_maintenance",
  AIRDROP_FARMING_FLOW_EXCHANGE_RISE_DN_MAINTENANCE: "airdrop_farming:flow:exchange_rise_dn_maintenance",
  CRYPTOCOM_REFEREE_HUB_ENABLE: "cryptocom_referee:hub:enable",
  CRYPTOCOM_REFEREE_HUB_REWARD_AMOUNT: "cryptocom_referee:hub:reward_amount",
  KRAKEN_PAGE_HUB_ENABLE: "kraken_page:hub:enable",
  COINBASE_PAGE_HUB_ENABLE: "coinbase_page:hub:enable",
  LBANK_PAGE_HUB_ENABLE: "lbank_page:hub:enable",
  CROSS_REDIRECT_FLOW_ENABLE: "cross_redirect:hub:flow_redirect_enable",
  REFERRAL_FLOW_ENABLE: "referral:flow:enable",
  LEADERBOARD_FLOW_ENABLE: "leaderboard:flow:enable",
  CEDE_FLOW_TRADE_AND_EARN_FLOW_ENABLE: "cede_flow_trade_and_earn:flow:enable",
  LEADERBOARD_FLOW_EXCHANGE_FILTER_ENABLE: "leaderboard:flow:exchange_filter_enable",
  AIRDROP_FARMING_FLOW_BACKTEST_RESULTS_ENABLE: "airdrop_farming:flow:backtest_results_enable",
  AIRDROP_FARMING_FLOW_FORCE_BOT_RATING: "airdrop_farming:flow:force_bot_rating",
  LEADERBOARD_PRICE_RECOMPUTE_ENABLED: "leaderboard:api:price_recompute_enabled",
  PERPS_HUB_DISABLE_TP_SL: "perps:hub:disable_tp_sl",
  PERPS_HUB_DISABLE_LIMIT_ORDERS: "perps:hub:disable_limit_orders",
  AIRDROP_FARMING_API_LEADERBOARD_ENABLE: "airdrop_farming:api:leaderboard_enable",
  DISCORD_LINK_FLOW_ENABLE: "socials:flow:discord_link_enable",
  X_LINK_FLOW_ENABLE: "socials:flow:x_link_enable",
  TELEGRAM_LINK_FLOW_ENABLE: "socials:flow:telegram_link_enable",
  X_LINK_ARENA_ENABLE: "socials:arena:x_link_enable",
  TELEGRAM_LINK_ARENA_ENABLE: "socials:arena:telegram_link_enable",
  CEDE_ARENA_API_ENABLE: "cede_arena:api:enable",
  /**
   * Gates the user-generated private competition rooms surface. The API guard
   * 404s every private-competition route when off; the Arena frontend mirrors
   * the same flag to hide the "Create Private Competition" entry point.
   */
  CEDE_ARENA_PRIVATE_COMPETITION_API_ENABLE: "cede_arena:api:private_competition_enable",
  /**
   * Gates the rising-drawdown format on private rooms: off ⇒ the API rejects a
   * room config carrying it and the Arena create/edit dialogs hide the option.
   * Rooms already carrying it keep it, including WAITING rooms, which still start
   * as rising: this is not a kill switch. CMS-scheduled competitions are not gated.
   */
  CEDE_ARENA_API_RISING_DRAWDOWN_ENABLE: "cede_arena:api:rising_drawdown_enable",
  /**
   * Kill switch for the CanberrAI auto-opponent. When off, the
   * auto-match cron is a no-op and the CMS CanberrAI config section is locked.
   */
  CEDE_ARENA_API_CANBERRAI_AUTO_MATCH_ENABLE: "cede_arena:api:canberrai_auto_match_enable",
  /**
   * Master gate for Cede Arena Spin & Go. When off, opt-in / claim paths refuse
   * to draw a pot multiplier (backend-enforced) and the CMS section is locked.
   * Combine with the per-category `enabled` config for finer control.
   */
  CEDE_ARENA_API_SPIN_AND_GO_ENABLE: "cede_arena:api:spin_and_go_enable",
  /**
   * Skips the ready-check decision on queue matches: every side whose matched
   * buy-in equals the one it queued at is confirmed at propose time, so the
   * duel starts without anyone clicking. A side matched at a different buy-in
   * (a downgrade) still confirms by hand — it is being offered
   * a price it did not pick. Directed rematches, onboarding duels and bot-only
   * tables are out of scope. Default off ⇒ today's 30s approval.
   */
  CEDE_ARENA_API_AUTO_ACCEPT_MATCH_ENABLE: "cede_arena:api:auto_accept_match_enable",
  /**
   * Kill switch for the bot Spin & Go table scheduler (simulated player
   * liquidity). When off, the scheduler cron is a no-op and the CMS bot
   * scheduler config section is locked.
   */
  CEDE_ARENA_API_CANBERRAI_BOT_COMPETITIONS_ENABLE: "cede_arena:api:canberrai_bot_competitions_enable",
  /** Backend gate for the lifetime bot-duel cap: off ⇒ the matchmaking tick ignores `botDuelMin/MaxLifetimeCap`. */
  CEDE_ARENA_API_CANBERRAI_BOT_DUEL_CAP_ENABLE: "cede_arena:api:canberrai_bot_duel_cap_enable",
  /**
   * Gates the post-battle directed rematch (DEV-646). When off, the propose /
   * eligibility routes 404 and the Arena recap hides the rematch CTA. Default off.
   */
  CEDE_ARENA_API_REMATCH_ENABLE: "cede_arena:api:rematch_enable",
  CEDE_ARENA_API_FORFEIT_ENABLE: "cede_arena:api:forfeit_enable",
  /** Cede Arena cross-promo banner on Hub's campaigns page. */
  CEDE_ARENA_HUB_PROMO_BANNER: "cede_arena:hub:promo_banner",
  CEDE_ARENA_ARENA_LEADERBOARD_ENABLE: "cede_arena:arena:leaderboard_enable",
  /**
   * Shows the settled-payouts feed on `/play`. `:arena:` rather than `:api:` ,
   * the endpoint stays served (it is gated by `cede_arena:api:enable` like every
   * other Arena read); this hides only the card, so the surface can be pulled
   * without an API deploy.
   */
  CEDE_ARENA_ARENA_PAYOUTS_FEED_ENABLE: "cede_arena:arena:payouts_feed_enable",
  /**
   * Gates the Arena quests page and its nav entry. `:arena:` rather than `:api:` —
   * the backend serves `app=arena` quests regardless; this only hides the surface.
   */
  CEDE_ARENA_ARENA_QUESTS_ENABLE: "cede_arena:arena:quests_enable",
  /** Arena UI gate for the referral ask and the referred-player confirmation. Pure presentation; the api flag still decides whether a code can be applied. */
  /**
   * Gates the fixed-week, per-buy-in leaderboard and the laurel crowns it feeds.
   * Off ⇒ /leaderboard is the all-time board alone and no crown query fires.
   */
  CEDE_ARENA_ARENA_WEEKLY_LEADERBOARD_ENABLE: "cede_arena:arena:weekly_leaderboard_enable",
  CEDE_ARENA_EMAIL_LOGIN_ENABLE: "cede_arena:arena:email_login_enable",
  /**
   * Arena's own sign-in surface (headless Privy hooks) in place of Privy's
   * stock modal: the welcome takeover's two-step and the returning-user
   * sign-in modal. Off ⇒ `login()` opens Privy's modal exactly as today.
   */
  CEDE_ARENA_CUSTOM_SIGN_IN_ENABLE: "cede_arena:arena:custom_sign_in_enable",
  CEDE_ARENA_GOOGLE_LOGIN_ENABLE: "cede_arena:arena:google_login_enable",
  CEDE_ARENA_X_LOGIN_ENABLE: "cede_arena:arena:x_login_enable",
  CEDE_ARENA_TELEGRAM_LOGIN_ENABLE: "cede_arena:arena:telegram_login_enable",
  CEDE_ARENA_COMPETITION_FLOW_ENABLE: "cede_arena_competition:flow:enable",
  /**
   * Unifold availability (card / Apple Pay / cross-chain crypto). Drives BOTH deposit
   * methods: it is the crypto method for everyone, and the card method — for the countries
   * listed below while Crossmint is on, and for everyone while it is off. Off ⇒ crypto is the legacy on-chain USDT transfer. The Unifold webhook
   * and the on-chain poller credit regardless. `:arena:` was correct while only the deposit
   * modal read it; this PR moved the only reader to the API. Kept unrenamed on purpose: the
   * row exists in every environment, and a renamed key would read the catalog default
   * (false) wherever the new row is missing, rerouting every Unifold country mid-rollout.
   */
  CEDE_ARENA_DEPOSIT_VIA_UNIFOLD_ENABLE: "cede_arena:arena:deposit_via_unifold_enable",
  /**
   * Crossmint onramp, the default card method. The API refuses Crossmint orders while
   * off; with Unifold also off there is no card method at all.
   */
  CEDE_ARENA_DEPOSIT_VIA_CROSSMINT_ENABLE: "cede_arena:api:deposit_via_crossmint_enable",
  /** CSV of ISO country codes whose CARD deposits go to Unifold while Crossmint is on. */
  CEDE_ARENA_DEPOSIT_UNIFOLD_COUNTRIES: "cede_arena:api:deposit_unifold_countries",
  /**
   * The legacy on-chain USDT transfer, which serves the crypto method while Unifold is off. The
   * row is seeded ON by migration rather than defaulted here, since an absent row reads as off:
   * turning it off is a deliberate retirement of the rail, and with the other two also off the
   * deposit modal has nothing to offer. The on-chain poller credits regardless.
   */
  CEDE_ARENA_DEPOSIT_VIA_ONCHAIN_ENABLE: "cede_arena:api:deposit_via_onchain_enable",
  /**
   * Newline-separated trading tips rotated in the Arena matchmaking waiting
   * room and get-ready overlay (one tip per line, keep each to one short
   * sentence). Empty/absent = Arena's built-in curated list.
   */
  CEDE_ARENA_MATCHMAKING_TRADING_TIPS: "cede_arena:arena:matchmaking_trading_tips",
  /**
   * Gates the free (Casual) matchmaking tier in the Arena stake picker. When
   * off, the Casual/Free tile is locked and only paid (Ranked) entry is
   * selectable. FE-only switch — no backend enforcement.
   */
  CEDE_ARENA_FREE_MATCHMAKING_ENABLE: "cede_arena:arena:free_matchmaking_enable",
  CEDE_ARENA_ANNOUNCEMENT_MODAL_ENABLE: "cede_arena:arena:announcement_modal_enable",
  CEDE_ARENA_CHAT_API_ENABLE: "cede_arena:api:chat_enable",
  /**
   * Chat AI moderation tier: on = paid LLM policy check (gpt-4o-mini) that
   * understands arena trash-talk vs insults; off = free OpenAI Moderation API
   * (weaker on standalone/French profanity). The word list always runs first.
   */
  CEDE_ARENA_CHAT_LLM_MODERATION_ENABLE: "cede_arena:api:chat_llm_moderation_enable",
  /** Backend gate for AI bot chat participants; when off the chat workers skip all bot reply/opener work. */
  CEDE_ARENA_CHAT_BOT_PARTICIPANTS_ENABLE: "cede_arena:api:chat_bot_participants_enable",
  CEDE_ARENA_CHAT_BOT_OPT_IN_COMPETITIONS_ENABLE: "cede_arena:api:chat_bot_opt_in_competitions_enable",
  CEDE_ARENA_API_REQUIRE_NICKNAME_ENABLE: "cede_arena:api:require_nickname_enable",
  /** Withholds an opponent's identity on a live proposal until both sides confirm. */
  CEDE_ARENA_API_HIDE_OPPONENT_IDENTITY_ENABLE: "cede_arena:api:hide_opponent_identity_enable",
  /** FE-only gate for the guided onboarding flow (welcome modal + tour). */
  CEDE_ARENA_ONBOARDING_FLOW_ENABLE: "cede_arena:arena:onboarding_flow_enable",
  CEDE_ARENA_MULTI_DUEL_ENABLE: "cede_arena:arena:multi_duel_enable",
  /** FE-only gate for the post-first-competition survey modal on the recap. */
  CEDE_ARENA_POST_COMPETITION_SURVEY_ENABLE: "cede_arena:arena:post_competition_survey_enable",
  CEDE_ARENA_QUALIFICATION_FORM_ENABLE: "cede_arena:arena:qualification_form_enable",
  /** Backend gate for Arena entry tickets (DEV-651): master switch for minting AND spending across starter packs, partner links and the quest. */
  CEDE_ARENA_API_ENTRY_TICKETS_ENABLE: "cede_arena:api:entry_tickets_enable",
  CEDE_ARENA_API_TICKET_LINKS_ENABLE: "cede_arena:api:ticket_links_enable",
  /** Backend gate for the DEV-734 ticket payout cap: on ⇒ a ticket-funded winner is paid its face value and the rest of the pot stays with the house. */
  CEDE_ARENA_API_TICKET_PAYOUT_CAP_ENABLE: "cede_arena:api:ticket_payout_cap_enable",
  CEDE_ARENA_API_REFERRALS_ENABLE: "cede_arena:api:referrals_enable",
  /** Backend gate for KOL managers: manager/invite routes refuse work when off; Arena mirrors it. Existing links keep accruing. */
  CEDE_ARENA_API_KOL_MANAGERS_ENABLE: "cede_arena:api:kol_managers_enable",
  /** Backend gate for Cede Arena clans: joins, leaves and tournament settlement refuse work when off; Arena mirrors it. */
  CEDE_ARENA_API_CLANS_ENABLE: "cede_arena:api:clans_enable",
  /** Backend gate for Arena instant withdrawals: when off every withdrawal takes the epoch + cooldown + claim path. */
  CEDE_ARENA_API_INSTANT_WITHDRAW_ENABLE: "cede_arena:api:instant_withdraw_enable",
  /** Backend gate for the Arena player profile card: the profile route 404s when off; Arena mirrors it to skip the hover trigger. */
  CEDE_ARENA_API_PLAYER_PROFILE_POPUP_ENABLE: "cede_arena:api:player_profile_popup_enable",
  /** Backend gate for the onboarding-quest reward grant (DEV-685): off ⇒ the grant command refuses work. */
  CEDE_ARENA_API_ONBOARDING_QUEST_ENABLE: "cede_arena:api:onboarding_quest_enable",
  /** Backend gate for the public community-Discord "someone joined the queue" feed: when off, the API skips the anonymous paid-queue-join post. */
  CEDE_ARENA_API_PUBLIC_QUEUE_JOIN_NOTIFICATION_ENABLE: "cede_arena:api:public_queue_join_notification_enable",
  /** Gates the Arena support chat (DEV-679): API routes 404 and the widget unmounts when off. */
  CEDE_ARENA_API_SUPPORT_CHAT_ENABLE: "cede_arena:api:support_chat_enable",
  /** Kill switch for the Cede Support AI agent: when off no agent run is enqueued. */
  CEDE_ARENA_API_SUPPORT_AGENT_ENABLE: "cede_arena:api:support_agent_enable",
  /** Support agent shadow mode: full pipeline, draft stored on the run, nothing posted to the user. */
  CEDE_ARENA_API_SUPPORT_AGENT_SHADOW: "cede_arena:api:support_agent_shadow",
  CEDE_ARENA_API_FRIENDS_ENABLE: "cede_arena:api:friends_enable",
  /** Independent gate for the SAME anonymous queue-join feed posted to the separate Cede Hub community Discord: when off, the API skips the Hub post. */
  CEDE_HUB_API_CEDE_ARENA_PUBLIC_QUEUE_JOIN_NOTIFICATION_ENABLE:
    "cede_hub:api:cede_arena_public_queue_join_notification_enable",
  /**
   * Recipient rule for a paid Arena competition's autopilot priority tickets,
   * minted when it finalizes: on ⇒ every non-bot entrant that played a positive
   * fee earns; off ⇒ only the rewarded ranks earn. Backend-enforced; Hub mirrors
   * it in the earning-schedule copy.
   */
  CEDE_ARENA_API_PRIORITY_TICKETS_ALL_PARTICIPANTS_ENABLE: "cede_arena:api:priority_tickets_all_participants_enable",
  /**
   * Big Arena Competition whitelist registrations. Backend-enforced on submit;
   * the `/100k` page mirrors it to swap the form for a "registrations closed" state.
   */
  CEDE_ARENA_API_BIG_COMPETITION_REGISTRATION_ENABLE: "cede_arena:api:big_competition_registration_enable",
  /**
   * The `/100k` promo banner in the duels lobby. Deliberately NOT tied to the
   * registration flag — the banner copy stays true whether or not the whitelist
   * is open, so the two are flipped independently. FE-only switch.
   */
  CEDE_ARENA_ARENA_BIG_COMPETITION_PROMO_BANNER_ENABLE: "cede_arena:arena:big_competition_promo_banner_enable",
  /**
   * Tournament engine kill switch: joins, CMS actions, and the scoring, round-close
   * and payout jobs. Off pauses the schedule-driven state machine; nothing is lost,
   * the jobs catch up when it flips back on.
   */
  CEDE_ARENA_API_TOURNAMENT_ENABLE: "cede_arena:api:tournament_enable",
  /** The `/tournament/[slug]` page and the lobby banner's switch to "Join the tournament". FE-only. */
  CEDE_ARENA_ARENA_TOURNAMENT_PAGE_ENABLE: "cede_arena:arena:tournament_page_enable",
  /** Gold highlight + hot-first sort in the battle market selector (top `hotPairsCount` of the battle's tier, else `ARENA_DEFAULT_HOT_PAIRS_COUNT`). FE-only. */
  CEDE_ARENA_ARENA_HOT_PAIRS_ENABLE: "cede_arena:arena:hot_pairs_enable",
  /** `/5k` sign-up registrations. Backend-enforced on submit; the page mirrors it to show "registrations closed". */
  CEDE_ARENA_API_TOURNAMENT_5K_REGISTRATION_ENABLE: "cede_arena:api:tournament_5k_registration_enable",
  /** The `/5k` prize pool in USDT. 0 is unset, and the page 404s. FE-only. */
  CEDE_ARENA_ARENA_TOURNAMENT_5K_PRIZE_POOL_USDT: "cede_arena:arena:tournament_5k_prize_pool_usdt",
  /** Favorites filter + star buttons in the duel market selector. FE-only. */
  CEDE_ARENA_ARENA_MARKET_FAVORITES_ENABLE: "cede_arena:arena:market_favorites_enable",
  /** Gates `GET /v1/cede-arena/feed`, the arena SSE leg and the UI; writers always record. */
  CEDE_ARENA_API_GLOBAL_FEED_ENABLE: "cede_arena:api:global_feed_enable",
  /** Backend gate for the GLOBAL chat room: send, feed CHAT items, SSE chat leg. */
  CEDE_ARENA_API_GLOBAL_CHAT_ENABLE: "cede_arena:api:global_chat_enable",
  /** Backend gate for non-roster posting in duel chats. */
  CEDE_ARENA_API_SPECTATOR_CHAT_ENABLE: "cede_arena:api:spectator_chat_enable",
  /** Backend gate for the chat opening `ARENA_CHAT_PRE_START_OPEN_MS` before a competition starts. */
  CEDE_ARENA_API_PRE_START_CHAT_ENABLE: "cede_arena:api:pre_start_chat_enable",
  /** The waiting stage's roster layout: who joined, the centred clock, and Room chat when pre-start chat is on. */
  CEDE_ARENA_ARENA_PRE_START_ROSTER_ENABLE: "cede_arena:arena:pre_start_roster_enable",
  /** FE-only switch for the arena mobile shell (bottom bar, Feed and Menu tabs). */
  CEDE_ARENA_ARENA_MOBILE_NAV_V2_ENABLE: "cede_arena:arena:mobile_nav_v2_enable",
  /** Battle Royale format. Backend refuses BR creation when off; the arena and CMS hide the format option. */
  CEDE_ARENA_API_BATTLE_ROYALE_ENABLE: "cede_arena:api:battle_royale_enable",
  /** FE-only switch for the simple (Up / Down) trade mode on the battle page. */
  CEDE_ARENA_ARENA_SIMPLE_TRADE_MODE_ENABLE: "cede_arena:arena:simple_trade_mode_enable",
  /** Classic PAID 1v1 duel. Off ⇒ the arena hides the PAID Standard entry and the API refuses human PAID opt-ins and rematches. */
  CEDE_ARENA_API_CLASSIC_PAID_DUEL_ENABLE: "cede_arena:api:classic_paid_duel_enable",
  /** Redacted opponent activity, its on-load endpoint, and live Arena position redaction + LIVE_PNL frames. Backend-enforced. */
  CEDE_ARENA_API_OPPONENT_ACTIVITY_ENABLE: "cede_arena:api:opponent_activity_enable",
  /**
   * FE-only switch for the live board rows and row card on Battle Royale, Spin & Go and classic rooms of three or
   * more (not duels).
   */
  CEDE_ARENA_ARENA_OPPONENT_LIVE_BOARD_ENABLE: "cede_arena:arena:opponent_live_board_enable",
  CEDE_ARENA_API_BATTLE_RECORD_ENABLE: "cede_arena:api:battle_record_enable",
  CEDE_ARENA_ARENA_POST_BATTLE_REVEAL_ENABLE: "cede_arena:arena:post_battle_reveal_enable",
  /** FE-only: the share-card editor on a settled result and the result-aware link preview of a battle page. */
  CEDE_ARENA_ARENA_SHARE_CARDS_ENABLE: "cede_arena:arena:share_cards_enable",
  /**
   * FE-only: the Battle Royale-led home at `/play`, hiding the Standard and Freeroll entry points.
   * Turn on together with `CEDE_ARENA_API_CLASSIC_PAID_DUEL_ENABLE` off; nothing enforces the pairing.
   */
  CEDE_ARENA_ARENA_BR_HOME_ENABLE: "cede_arena:arena:br_home_enable",
  /** FE-only: the waiting room (pre-start stage) on every competition page in place of the upcoming and entering overlays. */
  CEDE_ARENA_ARENA_WAITING_ROOM_ENABLE: "cede_arena:arena:waiting_room_enable",
  /** FE-only: Prepare, the prep desk opened from the waiting room. Inert without the waiting room. */
  CEDE_ARENA_ARENA_PRE_START_DESK_ENABLE: "cede_arena:arena:pre_start_desk_enable",
  /** Armed opening move. Off ⇒ the API refuses arming and the arena hides "Create opening move". */
  CEDE_ARENA_API_ARMED_OPENING_MOVE_ENABLE: "cede_arena:api:armed_opening_move_enable",
  /**
   * Gate for the `?exp_<key>=<variant>` QA pin. Its own flag, not the Arena kill
   * switch: that whitelist grants ACCESS, and reusing it would let anyone on it
   * pin variants on a live experiment. Whitelist = who may pin.
   */
  EXPERIMENT_API_QA_VARIANT_OVERRIDE_ENABLE: "experiment:api:qa_variant_override_enable",
} as const satisfies Record<string, keyof FeatureFlagCatalog>;
